/**
 * Report Button Component
 * 신고 플로우 UI
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Flag, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/analytics";

interface ReportButtonProps {
  reportedType: "article" | "comment" | "market_listing" | "community_talk" | "city_classified" | "user";
  reportedId: string | number;
  reportedUserId?: string;
  className?: string;
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "lg" | "default" | "icon";
}

const reportReasons = [
  { value: "spam", label: "스팸" },
  { value: "scam", label: "사기" },
  { value: "inappropriate", label: "부적절한 내용" },
  { value: "copyright", label: "저작권 침해" },
  { value: "fake", label: "허위 정보" },
  { value: "harassment", label: "괴롭힘" },
  { value: "illegal", label: "불법 내용" },
  { value: "other", label: "기타" },
] as const;

const reportCategories = [
  { value: "fraud", label: "사기" },
  { value: "harassment", label: "괴롭힘" },
  { value: "fake", label: "허위 정보" },
  { value: "offensive", label: "공격적 내용" },
  { value: "illegal", label: "불법" },
] as const;

export function ReportButton({
  reportedType,
  reportedId,
  reportedUserId,
  className,
  variant = "ghost",
  size = "sm",
}: ReportButtonProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!user) {
      alert("로그인이 필요합니다");
      return;
    }

    if (!reason || !category || !description.trim()) {
      alert("모든 필드를 입력해주세요");
      return;
    }

    setSubmitting(true);

    try {
      // The server resolves the reporter from this token (findOrCreateUser,
      // same as bookmarks/market/community-talk) rather than trusting a
      // client-supplied reporterId - reportedId/reportedUserId also need to
      // be real integers, not the Firebase UID string / stringified id this
      // used to send, since reports.reporter_id and reports.reported_id are
      // both integer columns and every submission was failing validation.
      const token = await user.getIdToken();
      const response = await fetch("/api/reports", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reportedType,
          reportedId: Number(reportedId),
          reportedUserId: reportedUserId ? Number(reportedUserId) : undefined,
          reason,
          category,
          description: description.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error("신고 제출 실패");
      }

      setSubmitted(true);
      trackEvent({
        event: "report_create",
        contentType: reportedType,
        contentId: String(reportedId),
        metadata: {
          reason,
          category,
        },
      });

      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setReason("");
        setCategory("");
        setDescription("");
      }, 2000);
    } catch (error) {
      console.error("Report submission error:", error);
      alert("신고 제출 중 오류가 발생했습니다");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={() => {
            if (!user) {
              alert("로그인이 필요합니다");
              return;
            }
          }}
        >
          <Flag className="h-4 w-4 mr-1" />
          신고
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>콘텐츠 신고</DialogTitle>
          <DialogDescription>
            부적절한 콘텐츠를 신고해주세요. 검토 후 조치하겠습니다.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p className="text-lg font-medium">신고가 접수되었습니다</p>
            <p className="text-sm text-muted-foreground mt-2">
              검토 후 조치하겠습니다
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">신고 사유</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id="reason">
                  <SelectValue placeholder="신고 사유를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {reportReasons.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">카테고리</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category">
                  <SelectValue placeholder="카테고리를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {reportCategories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">상세 설명</Label>
              <Textarea
                id="description"
                placeholder="신고 사유를 자세히 설명해주세요"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {!submitted && (
            <>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                취소
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? "제출 중..." : "신고하기"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

